import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import { fetchPassengers, deletePassenger, createPassenger, updatePassenger } from '../../../store/slices/passengerSlice';
import toast from 'react-hot-toast';

const MyPassengersPage = () => {
  const dispatch = useDispatch();
  const { items: passengers, loading } = useSelector((state) => state.passengers);
  const { user } = useSelector((state) => state.auth);
  
  const [showModal, setShowModal] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [isEditingBooker, setIsEditingBooker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 4;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'Mister'
  });

  // Separate booker from other passengers (matching PHP logic)
  const booker = passengers.find(p => 
    p.first_name === user?.first_name && 
    p.last_name === user?.last_name && 
    p.date_of_birth === user?.date_of_birth
  );
  
  const otherPassengers = passengers.filter(p => 
    p.first_name !== user?.first_name || 
    p.last_name !== user?.last_name || 
    p.date_of_birth !== user?.date_of_birth
  );

  // All displayable passengers: booker first, then others
  const allDisplayPassengers = [...(booker ? [{ ...booker, isBooker: true }] : []), ...otherPassengers];
  const totalPages = Math.ceil(allDisplayPassengers.length / ITEMS_PER_PAGE);
  const paginatedPassengers = allDisplayPassengers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    dispatch(fetchPassengers());
  }, [dispatch]);

  const handleEdit = (passenger, isBooker = false) => {
    setEditingPassenger(passenger);
    setIsEditingBooker(isBooker);
    setFormData({
      firstName: passenger.first_name?.toUpperCase() || '',
      lastName: passenger.last_name?.toUpperCase() || '',
      dateOfBirth: passenger.date_of_birth || '',
      gender: passenger.gender || 'Mister'
    });
    setShowModal(true);
  };

  const handleDelete = async (passengerId) => {
    if (window.confirm('Are you sure you want to delete this passenger?')) {
      try {
        await dispatch(deletePassenger(passengerId)).unwrap();
        toast.success('Passenger deleted successfully');
      } catch (error) {
        toast.error(error || 'Failed to delete passenger');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const passengerData = {
      first_name: formData.firstName.toUpperCase(),
      last_name: formData.lastName.toUpperCase(),
      date_of_birth: formData.dateOfBirth,
      gender: formData.gender
    };

    try {
      if (editingPassenger) {
        await dispatch(updatePassenger({ 
          id: editingPassenger.id, 
          passengerData 
        })).unwrap();
        toast.success('Passenger updated successfully');
      } else {
        await dispatch(createPassenger(passengerData)).unwrap();
        toast.success('Passenger added successfully');
      }
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error(error || 'Failed to save passenger');
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'Mister'
    });
    setEditingPassenger(null);
    setIsEditingBooker(false);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading && passengers.length === 0) {
    return (
      <div className="bg-jet-background pt-[40px] pb-8 px-3 sm:px-5">
        <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
          <ProfileSidebar />
          <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
              <p className="text-gray-600">Loading passengers...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-jet-background pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        <ProfileSidebar />

        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          <div className="bg-jet-yellow px-5 py-3 border-b border-yellow-200 rounded-t-[calc(0.25rem-1px)] flex items-center justify-center">
            <h3 className="text-[18px] font-semibold text-[#000] m-0">My Passengers</h3>
          </div>
          
          <div className="p-3 sm:p-5">
            <div className="space-y-3">
              {allDisplayPassengers.length === 0 ? (
                <div className="py-8 text-center text-gray-600">No passengers found.</div>
              ) : (
                paginatedPassengers.map((passenger) => (
                  <div key={passenger.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="font-medium text-jet-primary mb-1">
                        {passenger.first_name?.toUpperCase()} {passenger.last_name?.toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-600">
                        {passenger.date_of_birth?.toUpperCase()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(passenger, passenger.isBooker)}
                        className="bg-[#4285f4] hover:bg-[#357ae8] text-white px-3 py-2 rounded transition-colors"
                        title="Edit"
                      >
                        <i className="fas fa-pencil-alt"></i>
                      </button>
                      {!passenger.isBooker && (
                        <button
                          onClick={() => handleDelete(passenger.id)}
                          className="bg-[#db4437] hover:bg-[#c23321] text-white px-3 py-2 rounded transition-colors"
                          title="Delete"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-1 mt-4">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 flex items-center justify-center border rounded text-sm transition-colors ${
                      page === currentPage
                        ? 'bg-jet-yellow border-jet-yellow text-black font-bold'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
                >
                  ›
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit/Add Passenger Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[600px]">
            <form onSubmit={handleSubmit}>
              <div className="flex justify-between items-center p-4 border-b">
                <h4 className="text-lg font-semibold">
                  {editingPassenger 
                    ? `${editingPassenger.first_name?.toUpperCase()} ${editingPassenger.last_name?.toUpperCase()}`
                    : 'Add Passenger'
                  }
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fe fe-x text-xl"></i>
                </button>
              </div>

              <div className="p-4">
                {/* Gender */}
                <div className="mb-4 flex gap-8">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="male"
                      name="gender"
                      value="Mister"
                      checked={formData.gender === 'Mister'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      className="mr-2"
                      required
                    />
                    <label htmlFor="male" className="cursor-pointer">Male</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="female"
                      name="gender"
                      value="Miss"
                      checked={formData.gender === 'Miss'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      className="mr-2"
                      required
                    />
                    <label htmlFor="female" className="cursor-pointer">Female</label>
                  </div>
                </div>

                {/* First Name & Last Name */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value.replace(/\s/g, ''))}
                      placeholder="Enter first name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-jet-yellow focus:border-transparent uppercase"
                      pattern="^\S*$"
                      title="No spaces allowed"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value.replace(/\s/g, ''))}
                      placeholder="Enter last name"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-jet-yellow focus:border-transparent uppercase"
                      pattern="^\S*$"
                      title="No spaces allowed"
                      required
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Date Of Birth</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-jet-yellow focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end p-4 border-t">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-jet-yellow hover:bg-jet-yellow/90 text-jet-primary px-6 py-2 rounded font-semibold disabled:opacity-50 transition-colors"
                >
                  {loading ? 'SAVING...' : 'SAVE'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPassengersPage;